import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Users, Mail, Phone, MapPin, Upload, Download, Loader2, FileSpreadsheet, CheckCircle, AlertCircle, Edit, Trash2, Eye, ArrowUp, ArrowDown, Plus, UserPlus, Ban, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Parents = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const basePath = location.pathname.startsWith('/manager') ? '/manager/parents' : '/admin/parents';
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name'); // 'code' | 'name' | 'renewal'
    const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingParent, setEditingParent] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const fileInputRef = useRef(null);
    const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', mobile: '', address: '' });

    // Program filter
    const [programs, setPrograms] = useState([]);
    const [programFilter, setProgramFilter] = useState('all');

    // Add Guardian state
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [adding, setAdding] = useState(false);
    const [addForm, setAddForm] = useState({ first_name: '', last_name: '', email: '', mobile: '', address: '', reg_code: '' });
    const [codeCheck, setCodeCheck] = useState({ status: 'idle', message: '' });
    const codeCheckTimer = useRef(null);

    const checkCode = (code) => {
        clearTimeout(codeCheckTimer.current);
        if (!code.trim()) { setCodeCheck({ status: 'idle', message: '' }); return; }
        codeCheckTimer.current = setTimeout(async () => {
            setCodeCheck({ status: 'checking', message: 'Checking…' });
            try {
                const res = await axios.get(`${API_URL}/api/parents/check-code/${encodeURIComponent(code.trim())}`, { withCredentials: true });
                if (res.data.available) {
                    setCodeCheck({ status: 'ok', message: 'Available' });
                } else {
                    setCodeCheck({ status: 'taken', message: `Already used by ${res.data.owner}` });
                }
            } catch { setCodeCheck({ status: 'idle', message: '' }); }
        }, 400);
    };

    const openAddDialog = () => {
        setAddForm({ first_name: '', last_name: '', email: '', mobile: '', address: '', reg_code: '' });
        setCodeCheck({ status: 'idle', message: '' });
        setAddDialogOpen(true);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        if (codeCheck.status === 'taken') {
            toast.error('Reg code is already in use — choose another or leave it blank.');
            return;
        }
        setAdding(true);
        try {
            const payload = { ...addForm };
            if (!payload.reg_code.trim()) delete payload.reg_code;
            const res = await axios.post(`${API_URL}/api/parents`, payload, { withCredentials: true });
            toast.success(`Guardian created — Reg ${res.data.reg_code}`);
            setAddDialogOpen(false);
            fetchParents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create guardian');
        } finally { setAdding(false); }
    };

    useEffect(() => { fetchParents(); (async () => {
        try {
            const r = await axios.get(`${API_URL}/api/programs`, { withCredentials: true });
            setPrograms(r.data || []);
        } catch { /* silent */ }
    })(); }, []);

    const fetchParents = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/parents/enriched/list`, { withCredentials: true });
            setParents(response.data);
        } catch {
            try {
                const response = await axios.get(`${API_URL}/api/parents`, { withCredentials: true });
                setParents(response.data);
            } catch { toast.error('Failed to load parents'); }
        } finally { setLoading(false); }
    };

    const handleEdit = (parent) => {
        setEditingParent(parent);
        setEditForm({ first_name: parent.first_name || '', last_name: parent.last_name || '', email: parent.email || '', mobile: parent.mobile || '', address: parent.address || '' });
        setEditDialogOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/parents/${editingParent.id}`, editForm, { withCredentials: true });
            toast.success('Guardian updated');
            setEditDialogOpen(false);
            fetchParents();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this guardian?')) return;
        try {
            await axios.delete(`${API_URL}/api/parents/${id}`, { withCredentials: true });
            toast.success('Guardian deleted');
            fetchParents();
        } catch { toast.error('Failed to delete guardian'); }
    };

    const handleToggleSuspend = async (parent) => {
        const currentlySuspended = !!parent.is_suspended;
        const verb = currentlySuspended ? 'Reactivate' : 'Suspend';
        const msg = currentlySuspended
            ? `Reactivate ${parent.first_name} ${parent.last_name}? They will be able to log in again.`
            : `Suspend ${parent.first_name} ${parent.last_name}?\n\nThey (and any linked Guardian 2) will be blocked from logging in until reactivated.`;
        if (!window.confirm(msg)) return;
        try {
            await axios.put(
                `${API_URL}/api/parents/${parent.id}/suspend`,
                { suspended: !currentlySuspended },
                { withCredentials: true }
            );
            toast.success(`${verb}d`);
            fetchParents();
        } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${verb.toLowerCase()}`); }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/parents/upload/template`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'guardians_template.xlsx');
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
        } catch { toast.error('Failed to download template'); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true); setUploadResult(null);
        const formData = new FormData(); formData.append('file', file);
        try {
            const response = await axios.post(`${API_URL}/api/parents/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            setUploadResult(response.data);
            if (response.data.created > 0) { toast.success(`${response.data.created} guardian(s) imported`); fetchParents(); }
        } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const filteredParents = parents.filter(p => {
        // Program filter — matches parents having ≥1 student in the selected program
        if (programFilter !== 'all') {
            const pids = p.program_ids || [];
            if (!pids.includes(programFilter)) return false;
        }
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;
        const digits = q.replace(/\D/g, '');
        const mobile1 = (p.mobile || '').replace(/\D/g, '');
        const mobile2 = (p.guardian2_mobile || '').replace(/\D/g, '');
        return (
            p.first_name?.toLowerCase().includes(q) ||
            p.last_name?.toLowerCase().includes(q) ||
            p.guardian2_first_name?.toLowerCase().includes(q) ||
            p.guardian2_last_name?.toLowerCase().includes(q) ||
            p.email?.toLowerCase().includes(q) ||
            p.guardian2_email?.toLowerCase().includes(q) ||
            p.reg_code?.includes(searchQuery) ||
            (digits && (mobile1.includes(digits) || mobile2.includes(digits)))
        );
    });

    const sortedParents = [...filteredParents].sort((a, b) => {
        let cmp = 0;
        if (sortBy === 'code') {
            cmp = String(a.reg_code || '').localeCompare(String(b.reg_code || ''), undefined, { numeric: true });
        } else if (sortBy === 'renewal') {
            // Renewed first when desc, Not Renewed first when asc
            cmp = (a.has_renewed === b.has_renewed) ? 0 : (a.has_renewed ? 1 : -1);
        } else { // 'name'
            const an = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
            const bn = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
            cmp = an.localeCompare(bn);
        }
        return sortDir === 'asc' ? cmp : -cmp;
    });

    const toggleSort = (key) => {
        if (sortBy === key) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDir('asc');
        }
    };
    const SortBtn = ({ k, label }) => (
        <Button
            size="sm"
            variant={sortBy === k ? 'default' : 'outline'}
            className={sortBy === k ? 'bg-[#064E3B] hover:bg-[#047857] text-white' : ''}
            onClick={() => toggleSort(k)}
            data-testid={`sort-by-${k}-btn`}
        >
            {label}
            {sortBy === k && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />)}
        </Button>
    );

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parents-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Guardians</h1>
                        <p className="text-[#4B5563]">Manage all registered guardians</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Badge variant="secondary"><Users className="h-4 w-4 mr-1" />{parents.length} guardians</Badge>
                        <Button onClick={openAddDialog} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-guardian-btn">
                            <UserPlus className="h-4 w-4 mr-2" />Add Guardian
                        </Button>
                        <Button
                            variant="outline"
                            onClick={async () => {
                                try {
                                    const res = await axios.get(`${API_URL}/api/parents/export/excel`, { withCredentials: true, responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                    const link = document.createElement('a');
                                    link.href = url; link.setAttribute('download', 'guardians.xlsx');
                                    document.body.appendChild(link); link.click(); link.remove();
                                    window.URL.revokeObjectURL(url);
                                    toast.success('Guardians exported');
                                } catch { toast.error('Failed to export'); }
                            }}
                            data-testid="export-parents-btn"
                        >
                            <Download className="h-4 w-4 mr-2" />Export Excel
                        </Button>
                        <Button variant="outline" onClick={() => { setUploadResult(null); setUploadDialogOpen(true); }} data-testid="upload-parents-btn"><Upload className="h-4 w-4 mr-2" /> Upload Excel</Button>
                        {user?.role === 'super_admin' && (
                            <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={async () => { if (!window.confirm('DELETE ALL guardians? This cannot be undone!')) return; if (!window.confirm('Are you absolutely sure?')) return; try { await axios.delete(`${API_URL}/api/parents`, { withCredentials: true }); toast.success('All guardians deleted'); fetchParents(); } catch { toast.error('Failed'); } }} data-testid="delete-all-parents-btn"><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                        )}
                    </div>
                </div>

                <Card className="card-surface"><CardContent className="p-4 space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                        <Input
                            placeholder="Search by name, email, mobile (G1 or G2), or reg code..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="parents-search-input"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-[#4B5563] mr-1">Sort by:</span>
                        <SortBtn k="code" label="Code" />
                        <SortBtn k="name" label="Name" />
                        <SortBtn k="renewal" label="Renewal status" />
                        <span className="text-xs text-[#4B5563] ml-4 mr-1">Program:</span>
                        <select
                            value={programFilter}
                            onChange={(e) => setProgramFilter(e.target.value)}
                            className="text-sm border border-[#D1D5DB] rounded-md px-2 py-1 bg-white hover:bg-[#FAF9F6]"
                            data-testid="parents-program-filter"
                        >
                            <option value="all">All programs</option>
                            {programs.map(pr => (<option key={pr.id} value={pr.id}>{pr.name}</option>))}
                        </select>
                        {programFilter !== 'all' && <button type="button" className="text-xs text-[#7C3AED] underline" onClick={() => setProgramFilter('all')}>Clear</button>}
                    </div>
                </CardContent></Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {sortedParents.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Guardian</TableHead>
                                            <TableHead>Reg Code</TableHead>
                                            <TableHead>Contact</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {sortedParents.map((parent) => (
                                            <TableRow key={parent.id} className="cursor-pointer hover:bg-[#F9FAFB]" onClick={() => navigate(`${basePath}/${parent.id}`)}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#064E3B] flex items-center justify-center text-white font-medium">{parent.first_name?.[0]}{parent.last_name?.[0]}</div>
                                                        <div>
                                                            <p className="font-medium">{parent.first_name} {parent.last_name}</p>
                                                            {(parent.guardian2_first_name || parent.guardian2_last_name) && <p className="text-xs text-[#064E3B]">{parent.guardian2_first_name} {parent.guardian2_last_name}</p>}
                                                            <p className="text-xs text-[#4B5563]">{parent.email}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell><Badge variant="outline" className="tabular-nums">{parent.reg_code}</Badge></TableCell>
                                                <TableCell>
                                                    <div className="flex items-center text-sm"><Phone className="h-3 w-3 mr-1 text-[#4B5563]" />{parent.mobile}</div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {parent.is_suspended ? (
                                                            <Badge className="bg-[#6B7280] text-white text-xs" data-testid={`suspended-badge-${parent.id}`}>Suspended</Badge>
                                                        ) : null}
                                                        {parent.payment_status === 'unpaid' ? (
                                                            <Badge className="bg-[#EF4444] text-white text-xs">Unpaid</Badge>
                                                        ) : (
                                                            <Badge className="bg-[#10B981] text-white text-xs">Paid</Badge>
                                                        )}
                                                        {parent.has_renewed ? (
                                                            <Badge className="bg-[#064E3B] text-white text-xs">Renewed</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="text-xs text-[#F59E0B] border-[#F59E0B]">Not Renewed</Badge>
                                                        )}
                                                        {parent.is_external ? (
                                                            <Badge variant="outline" className="text-xs text-[#9A3412] border-[#FED7AA]" data-testid={`external-badge-${parent.id}`}>External</Badge>
                                                        ) : null}
                                                    </div>
                                                    <p className="text-[11px] text-[#4B5563] mt-1 tabular-nums" data-testid={`last-login-${parent.id}`}>
                                                        {parent.last_login_at
                                                            ? `Last login: ${new Date(parent.last_login_at).toLocaleString([], { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                                                            : <span className="italic text-[#9CA3AF]">Never logged in</span>}
                                                    </p>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                                                        <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/${parent.id}`)} data-testid={`view-parent-${parent.id}`}><Eye className="h-4 w-4" /></Button>
                                                        <Button size="sm" variant="ghost" onClick={() => handleEdit(parent)}><Edit className="h-4 w-4" /></Button>
                                                        {user?.role === 'super_admin' && (
                                                            parent.is_suspended ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="text-[#10B981]"
                                                                    onClick={() => handleToggleSuspend(parent)}
                                                                    title="Reactivate account"
                                                                    data-testid={`reactivate-${parent.id}`}
                                                                ><ShieldCheck className="h-4 w-4" /></Button>
                                                            ) : (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="text-[#F59E0B]"
                                                                    onClick={() => handleToggleSuspend(parent)}
                                                                    title="Suspend account"
                                                                    data-testid={`suspend-${parent.id}`}
                                                                ><Ban className="h-4 w-4" /></Button>
                                                            )
                                                        )}
                                                        <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDelete(parent.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12"><Users className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No guardians found</p></div>
                        )}
                    </CardContent>
                </Card>

                {/* Edit Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Edit Guardian</DialogTitle></DialogHeader>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2"><Label>First Name</Label><Input value={editForm.first_name} onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} required /></div>
                                <div className="space-y-2"><Label>Last Name</Label><Input value={editForm.last_name} onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} required /></div>
                            </div>
                            <div className="space-y-2"><Label>Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} required /></div>
                            <div className="space-y-2"><Label>Mobile</Label><Input value={editForm.mobile} onChange={(e) => setEditForm({...editForm, mobile: e.target.value})} /></div>
                            <div className="space-y-2"><Label>Address</Label><Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} /></div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Update</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Add Guardian Dialog */}
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#064E3B]" /> Add Guardian</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddSubmit} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5"><Label>First Name *</Label><Input value={addForm.first_name} onChange={(e) => setAddForm({...addForm, first_name: e.target.value})} required data-testid="add-guardian-first-name" /></div>
                                <div className="space-y-1.5"><Label>Last Name *</Label><Input value={addForm.last_name} onChange={(e) => setAddForm({...addForm, last_name: e.target.value})} required data-testid="add-guardian-last-name" /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={addForm.email} onChange={(e) => setAddForm({...addForm, email: e.target.value.toLowerCase()})} required data-testid="add-guardian-email" /></div>
                                <div className="space-y-1.5"><Label>Mobile *</Label><Input value={addForm.mobile} onChange={(e) => setAddForm({...addForm, mobile: e.target.value})} required data-testid="add-guardian-mobile" /></div>
                            </div>
                            <div className="space-y-1.5"><Label>Address</Label><Input value={addForm.address} onChange={(e) => setAddForm({...addForm, address: e.target.value})} data-testid="add-guardian-address" /></div>
                            <div className="space-y-1.5">
                                <Label>Reg Code (optional — auto-generates max + 1 if blank)</Label>
                                <Input
                                    value={addForm.reg_code}
                                    onChange={(e) => { setAddForm({...addForm, reg_code: e.target.value}); checkCode(e.target.value); }}
                                    placeholder="e.g. 24879"
                                    className="font-mono"
                                    data-testid="add-guardian-reg-code"
                                />
                                {codeCheck.status === 'taken' && (<p className="text-xs text-[#EF4444]" data-testid="reg-code-taken">✗ {codeCheck.message}</p>)}
                                {codeCheck.status === 'ok' && (<p className="text-xs text-[#10B981]">✓ {codeCheck.message}</p>)}
                                {codeCheck.status === 'checking' && (<p className="text-xs text-[#6B7280]">{codeCheck.message}</p>)}
                            </div>
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-xs text-[#166534]">
                                A random password is generated. The guardian will set their own via <strong>Forgot Password</strong> on the login page.
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={adding || codeCheck.status === 'taken'} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-guardian-submit">
                                    {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Create Guardian
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Upload Dialog */}
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#064E3B]" /> Upload Guardians Excel</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]"><p className="font-medium mb-1">Required columns:</p><p>first_name, last_name, email, mobile</p></div>
                            <Button variant="outline" className="w-full" onClick={handleDownloadTemplate}><Download className="h-4 w-4 mr-2" /> Download Template</Button>
                            <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" id="parents-file-upload" />
                                <label htmlFor="parents-file-upload" className="cursor-pointer">
                                    {uploading ? <div className="flex flex-col items-center"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mb-2" /><p className="text-sm">Uploading...</p></div> : <div className="flex flex-col items-center"><Upload className="h-8 w-8 text-[#4B5563] mb-2" /><p className="text-sm font-medium">Click to upload</p></div>}
                                </label>
                            </div>
                            {uploadResult && (<div className="space-y-2"><div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-[#10B981]" /><span>{uploadResult.created} created</span></div>{uploadResult.skipped > 0 && <div className="flex items-center gap-2 text-sm text-[#F59E0B]"><AlertCircle className="h-4 w-4" /><span>{uploadResult.skipped} skipped</span></div>}{uploadResult.errors?.length > 0 && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 max-h-32 overflow-y-auto">{uploadResult.errors.map((e, i) => <p key={`err-${i}-${e.slice(0,30)}`} className="text-xs text-[#DC2626]">{e}</p>)}</div>}</div>)}
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Parents;
